/**
 * oRPC Client — 全 Contract を統合した単一クライアント
 *
 * 新しい API を追加する場合:
 * 1. Contract を定義（共有パッケージ or ローカル）
 * 2. appContract に追加
 * 3. client.xxx.method() で呼び出し
 */
import { createORPCClient } from '@orpc/client';
import { oc, type ContractRouterClient } from '@orpc/contract';
import { OpenAPILink } from '@orpc/openapi-client/fetch';
import {
  appIntegrationsForSelectContract,
  chatContract,
  dataPipelineContract,
  dataPipelineEnumsContract,
  dealLedgerContract,
  findPersonHistoriesFromEventStoreContract,
  storageSourceTypesForSelectContract,
  individualEquipmentContract,
  listDeviceCostByDepartmentContract,
  listDeviceCostByMemberContract,
  listPeopleOperationActivityContract,
  listPeopleOperationActivityTypesContract,
  oauth2Contract,
  openidMetadataContract,
  outboundDataPipelineConfigContract,
  outboundDataPipelineTaskContract,
  peopleCsvContract,
  s3Contract,
  softwareContract,
  summaryPeopleOperationActivityContract,
  supplierLedgerContract,
  supplierLedgerSettingsContract,
  workModeContract,
  loginEmailPolicyContract,
} from '@standandforce/api-contract';

import { authFetch, getBaseUrl } from './auth-fetch';
import {
  getAppIntegrationGwsConnectionRoute,
  patchAppIntegrationGwsConnectionRoute,
} from './contracts/app-integration-gws-connection';
import { contractLedgerContract } from './contracts/contract-ledger';
import { contractOverviewContract } from './contracts/contract-overview';
import { dataPipelineCsvContract } from './contracts/data-pipeline-csv';
import { govProfileSubmittableContract } from './contracts/gov-profile-submittable';
import { laborCoreEgovApplicationStepsContract } from './contracts/labor-core-egov-application-steps';
import { laborCoreEgovSubmitContract } from './contracts/labor-core-egov-submit';
import { laborCoreYearEndAdjustmentInstancesContract } from './contracts/labor-core-year-end-adjustment-instances';
import { placeOverviewContract } from './contracts/place-overview';

/**
 * 全 Contract を 1 つの Router にマージ。
 * ファイルアップロード用ローカル Contract と共有パッケージ Contract を統合。
 */
export const appContract = oc.router({
  workMode: workModeContract,
  s3: s3Contract,
  peopleCsv: peopleCsvContract,
  dataPipelineCsv: dataPipelineCsvContract,
  dataPipeline: dataPipelineContract,
  dataPipelineEnums: dataPipelineEnumsContract,
  outboundDataPipeline: outboundDataPipelineConfigContract,
  outboundDataPipelineTask: outboundDataPipelineTaskContract,
  appIntegrationsForSelect: appIntegrationsForSelectContract,
  storageSourceTypesForSelect: storageSourceTypesForSelectContract,
  contractLedger: contractLedgerContract,
  contractOverview: contractOverviewContract,
  loginEmailPolicy: loginEmailPolicyContract,
  software: softwareContract,
  appIntegration: oc.router({
    gwsConnection: patchAppIntegrationGwsConnectionRoute,
    getGwsConnection: getAppIntegrationGwsConnectionRoute,
  }),
  individualEquipment: individualEquipmentContract,
  deviceCost: oc.router({
    byMember: listDeviceCostByMemberContract,
    byDepartment: listDeviceCostByDepartmentContract,
  }),
  listPeopleOperationActivity: listPeopleOperationActivityContract,
  listPeopleOperationActivityTypes: listPeopleOperationActivityTypesContract,
  placeOverview: placeOverviewContract,
  summaryPeopleOperationActivity: summaryPeopleOperationActivityContract,
  dealLedger: dealLedgerContract,
  supplierLedger: {
    suppliersList: supplierLedgerContract.suppliersList,
    suppliersCounts: supplierLedgerContract.suppliersCounts,
    suppliers: {
      getProfileHeader: supplierLedgerContract.supplierProfileHeader,
      getDetail: supplierLedgerContract.supplierDetail,
      update: supplierLedgerContract.supplierUpdate,
      avlEntries: {
        create: supplierLedgerContract.avlEntryCreate,
        update: supplierLedgerContract.avlEntryUpdate,
        delete: supplierLedgerContract.avlEntryDelete,
      },
    },
    settings: supplierLedgerSettingsContract,
  },
  laborCoreEgovApplicationSteps: laborCoreEgovApplicationStepsContract,
  laborCoreEgovSubmit: laborCoreEgovSubmitContract,
  laborCoreYearEndAdjustmentInstances:
    laborCoreYearEndAdjustmentInstancesContract,
  govProfileSubmittable: govProfileSubmittableContract,
  personAggregateEvents: findPersonHistoriesFromEventStoreContract,
  aiOperatorChat: chatContract,
  oauth2: oauth2Contract,
  openidMetadata: openidMetadataContract,
});

const link = new OpenAPILink(appContract, {
  url: getBaseUrl() ?? '',
  fetch: authFetch,
});

export const orpcClient: ContractRouterClient<typeof appContract> =
  createORPCClient(link);
